import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import './EmailQueueManagement.css';

interface QueueStats {
  pending: number;
  processing: number;
  sent: number;
  failed: number;
  cancelled: number;
  adminReview: number;
  total: number;
}

interface QueueEntry {
  id: number;
  bookingId: number;
  bookingNumber: string;
  customerName: string;
  customerEmail: string;
  templateName: string;
  templateDisplayName: string;
  status: string;
  scheduledFor: string;
  processAfter: string | null;
  attempts: number;
  maxAttempts: number;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;
}

interface QueueResponse {
  data: QueueEntry[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

const STATUS_LABELS: Record<string, string> = {
  Pending: 'Wartend',
  Processing: 'In Bearbeitung',
  Sent: 'Gesendet',
  Failed: 'Fehlgeschlagen',
  Cancelled: 'Abgebrochen',
  AdminReview: 'Admin-Freigabe',
};

const getStatusClass = (status: string): string => {
  const map: Record<string, string> = {
    Pending: 'status-pending',
    Processing: 'status-processing',
    Sent: 'status-sent',
    Failed: 'status-failed',
    Cancelled: 'status-cancelled',
    AdminReview: 'status-adminreview',
  };
  return map[status] || '';
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const EmailQueueManagement: React.FC = () => {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [smtpStatus, setSmtpStatus] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('admin@frankenfloss.de');
  const [testEmailResult, setTestEmailResult] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Action menu + modals
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const [detailEntry, setDetailEntry] = useState<any | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEmailCorrectionModal, setShowEmailCorrectionModal] = useState(false);
  const [correctionEmail, setCorrectionEmail] = useState('');
  const [correctionEntryId, setCorrectionEntryId] = useState<number | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/queue/stats`, { headers: getAuthHeaders() });
      setStats(res.data);
    } catch (err) {
      console.error('Stats laden fehlgeschlagen:', err);
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get<QueueResponse>(`${API_BASE_URL}/email/queue?${params}`, { headers: getAuthHeaders() });
      setEntries(res.data.data);
      setTotalCount(res.data.totalCount);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error('Queue laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchStats(); fetchQueue(); }, [fetchStats, fetchQueue]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleApprove = async (id: number) => {
    setActionLoading(id);
    try {
      await axios.post(`${API_BASE_URL}/email/queue/${id}/approve`, {}, { headers: getAuthHeaders() });
      showMessage(`E-Mail #${id} freigegeben`, 'success');
      fetchStats(); fetchQueue();
    } catch { showMessage('Freigabe fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (id: number) => {
    setActionLoading(id);
    try {
      await axios.post(`${API_BASE_URL}/email/queue/${id}/reject`, {}, { headers: getAuthHeaders() });
      showMessage(`E-Mail #${id} abgelehnt`, 'success');
      fetchStats(); fetchQueue();
    } catch { showMessage('Ablehnung fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleRetry = async (id: number) => {
    if (!window.confirm('E-Mail erneut senden? Bisherige Fehlversuche werden zurückgesetzt.')) return;
    setActionLoading(id);
    try {
      await axios.post(`${API_BASE_URL}/email/queue/${id}/retry`, {}, { headers: getAuthHeaders() });
      showMessage(`E-Mail #${id} wird erneut gesendet`, 'success');
      fetchStats(); fetchQueue();
    } catch { showMessage('Erneutes Senden fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`E-Mail #${id} wirklich löschen?`)) return;
    setActionLoading(id);
    try {
      await axios.delete(`${API_BASE_URL}/email/queue/${id}`, { headers: getAuthHeaders() });
      showMessage(`E-Mail #${id} gelöscht`, 'success');
      fetchStats(); fetchQueue();
    } catch { showMessage('Löschen fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handleViewDetails = async (entry: QueueEntry) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/email/queue/${entry.id}`, { headers: getAuthHeaders() });
      setDetailEntry(res.data);
      setShowDetailModal(true);
    } catch { showMessage('Details konnten nicht geladen werden', 'error'); }
  };

  const handleOpenEmailCorrection = (entry: QueueEntry) => {
    setCorrectionEntryId(entry.id);
    setCorrectionEmail(entry.customerEmail);
    setShowEmailCorrectionModal(true);
  };

  const handleSubmitEmailCorrection = async () => {
    if (!correctionEntryId || !correctionEmail) return;
    setActionLoading(correctionEntryId);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/email/queue/${correctionEntryId}/update-email`,
        { newEmail: correctionEmail },
        { headers: getAuthHeaders() }
      );
      showMessage(res.data.message, 'success');
      setShowEmailCorrectionModal(false);
      fetchStats(); fetchQueue();
    } catch { showMessage('E-Mail-Korrektur fehlgeschlagen', 'error'); }
    finally { setActionLoading(null); }
  };

  const handlePurge = async () => {
    if (!window.confirm('Alte fehlgeschlagene/abgebrochene E-Mails bereinigen?')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/email/queue/purge?olderThanDays=7`, { headers: getAuthHeaders() });
      showMessage(`${res.data.deletedCount} Einträge bereinigt`, 'success');
      fetchStats(); fetchQueue();
    } catch { showMessage('Bereinigung fehlgeschlagen', 'error'); }
  };

  const handleSmtpTest = async () => {
    setSmtpStatus('testing');
    try {
      const res = await axios.get(`${API_BASE_URL}/email/test-connection`, { headers: getAuthHeaders() });
      setSmtpStatus(res.data.success ? 'ok' : 'error');
    } catch { setSmtpStatus('error'); }
  };

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestEmailResult('sending');
    try {
      await axios.post(`${API_BASE_URL}/email/send-test`, { email: testEmail }, { headers: getAuthHeaders() });
      setTestEmailResult('ok');
    } catch { setTestEmailResult('error'); }
  };

  const smtpBtnClass = `btn-smtp${smtpStatus === 'ok' ? ' smtp-ok' : smtpStatus === 'error' ? ' smtp-error' : ''}`;

  return (
    <div className="email-management">
      {/* Header */}
      <div className="email-header">
        <h1>E-Mail-Verwaltung</h1>
        <div className="header-actions">
          <button className={smtpBtnClass} onClick={handleSmtpTest}>
            {smtpStatus === 'testing' ? 'Teste...' : smtpStatus === 'ok' ? 'SMTP OK' : smtpStatus === 'error' ? 'SMTP Fehler' : 'SMTP testen'}
          </button>
          <button className="btn-secondary" onClick={handlePurge}>Bereinigen</button>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div className={`email-message ${message.type}`}>{message.text}</div>
      )}

      {/* Stats Cards — CLICKABLE FILTER */}
      {stats && (
        <div className="email-stats">
          {[
            { label: 'Wartend', value: stats.pending, color: '#f0ad4e', filter: 'Pending' },
            { label: 'Admin-Review', value: stats.adminReview, color: '#6C3483', filter: 'AdminReview' },
            { label: 'In Bearbeitung', value: stats.processing, color: '#2471A3', filter: 'Processing' },
            { label: 'Gesendet', value: stats.sent, color: '#1E8449', filter: 'Sent' },
            { label: 'Fehlgeschlagen', value: stats.failed, color: '#C0392B', filter: 'Failed' },
            { label: 'Gesamt', value: stats.total, color: '#666', filter: '' },
          ].map(s => (
            <div
              key={s.label}
              className={`email-stat-card${statusFilter === s.filter ? ' active' : ''}`}
              style={{ borderTopColor: s.color }}
              onClick={() => { setStatusFilter(s.filter); setPage(1); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') { setStatusFilter(s.filter); setPage(1); } }}
            >
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Test E-Mail */}
      <div className="email-test-card">
        <h3>Test-E-Mail senden</h3>
        <div className="test-email-row">
          <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@frankenfloss.de" />
          <button className="btn-primary" onClick={handleSendTest}>
            {testEmailResult === 'sending' ? 'Sende...' : testEmailResult === 'ok' ? 'Gesendet!' : testEmailResult === 'error' ? 'Fehler!' : 'Senden'}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="email-filter-bar">
        <select className="status-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">Alle Status</option>
          <option value="Pending">Wartend</option>
          <option value="AdminReview">Admin-Freigabe</option>
          <option value="Processing">In Bearbeitung</option>
          <option value="Sent">Gesendet</option>
          <option value="Failed">Fehlgeschlagen</option>
          <option value="Cancelled">Abgebrochen</option>
        </select>
        <span className="entry-count">{totalCount} E-Mails</span>
      </div>

      {/* Table */}
      <div className="email-table-container">
        <table className="email-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Buchung</th>
              <th>Kunde</th>
              <th>Template</th>
              <th>Status</th>
              <th>Erstellt</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="cell-loading">Lade E-Mails...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="cell-loading">Keine Einträge gefunden</td></tr>
            ) : entries.map(entry => (
              <tr key={entry.id}>
                <td className="cell-muted">#{entry.id}</td>
                <td className="cell-bold">{entry.bookingNumber}</td>
                <td>
                  <div>{entry.customerName}</div>
                  <div className="cell-sub">{entry.customerEmail}</div>
                </td>
                <td>{entry.templateDisplayName}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(entry.status)}`}>
                    {STATUS_LABELS[entry.status] || entry.status}
                  </span>
                  {entry.errorMessage && (
                    <div className="error-hint" title={entry.errorMessage}>{entry.errorMessage}</div>
                  )}
                </td>
                <td className="cell-muted">{formatDate(entry.createdAt)}</td>
                <td className={openDropdownId === entry.id ? 'action-cell-open' : ''}>
                  <div className={`action-menu${openDropdownId === entry.id ? ' open' : ''}`}>
                    <button
                      className="btn-action-menu"
                      onClick={(e) => { e.stopPropagation(); setOpenDropdownId(openDropdownId === entry.id ? null : entry.id); }}
                      title="Aktionen"
                    >
                      <span>&#x22EF;</span>
                    </button>
                    {openDropdownId === entry.id && (
                      <div className="action-dropdown">
                        <button className="dropdown-item" onClick={(e) => { e.stopPropagation(); handleViewDetails(entry); setOpenDropdownId(null); }}>
                          <span className="dropdown-icon">&#128065;</span><span>Anzeigen</span>
                        </button>

                        {(entry.status === 'AdminReview' || entry.status === 'Pending') && (
                          <button className="dropdown-item dropdown-item-success" disabled={actionLoading === entry.id}
                            onClick={(e) => { e.stopPropagation(); handleApprove(entry.id); setOpenDropdownId(null); }}>
                            <span className="dropdown-icon">&#9989;</span><span>Freigeben</span>
                          </button>
                        )}

                        {(entry.status === 'AdminReview' || entry.status === 'Pending') && (
                          <button className="dropdown-item" disabled={actionLoading === entry.id}
                            onClick={(e) => { e.stopPropagation(); handleReject(entry.id); setOpenDropdownId(null); }}>
                            <span className="dropdown-icon">&#10060;</span><span>Ablehnen</span>
                          </button>
                        )}

                        <div className="dropdown-divider"></div>

                        {entry.status !== 'Processing' && (
                          <button className="dropdown-item" disabled={actionLoading === entry.id}
                            onClick={(e) => { e.stopPropagation(); handleRetry(entry.id); setOpenDropdownId(null); }}>
                            <span className="dropdown-icon">&#128260;</span><span>Erneut senden</span>
                          </button>
                        )}

                        {['Failed', 'Cancelled', 'AdminReview', 'Pending'].includes(entry.status) && (
                          <button className="dropdown-item"
                            onClick={(e) => { e.stopPropagation(); handleOpenEmailCorrection(entry); setOpenDropdownId(null); }}>
                            <span className="dropdown-icon">&#9999;&#65039;</span><span>E-Mail korrigieren</span>
                          </button>
                        )}

                        <div className="dropdown-divider"></div>

                        {entry.status !== 'Processing' && (
                          <button className="dropdown-item dropdown-item-danger" disabled={actionLoading === entry.id}
                            onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); setOpenDropdownId(null); }}>
                            <span className="dropdown-icon">&#128465;</span><span>Löschen</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="email-pagination">
            <span className="page-info">{totalCount} Einträge, Seite {page} von {totalPages}</span>
            <div className="page-buttons">
              <button className="btn-page" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>Zurück</button>
              <button className="btn-page" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Weiter</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && detailEntry && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content email-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>E-Mail #{detailEntry.id} — Details</h2>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-row"><span className="detail-label">Buchung:</span><span className="detail-value">{detailEntry.bookingNumber}</span></div>
                <div className="detail-row"><span className="detail-label">Kunde:</span><span className="detail-value">{detailEntry.customerName}</span></div>
                <div className="detail-row"><span className="detail-label">E-Mail:</span><span className="detail-value">{detailEntry.customerEmail}</span></div>
                <div className="detail-row"><span className="detail-label">Template:</span><span className="detail-value">{detailEntry.templateDisplayName}</span></div>
                <div className="detail-row"><span className="detail-label">Betreff:</span><span className="detail-value">{detailEntry.templateSubject}</span></div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value"><span className={`status-badge ${getStatusClass(detailEntry.status)}`}>{STATUS_LABELS[detailEntry.status] || detailEntry.status}</span></span>
                </div>
                <div className="detail-row"><span className="detail-label">Priorität:</span><span className="detail-value">{detailEntry.priority}</span></div>
                <div className="detail-row"><span className="detail-label">Erstellt:</span><span className="detail-value">{formatDate(detailEntry.createdAt)}</span></div>
                <div className="detail-row"><span className="detail-label">Geplant für:</span><span className="detail-value">{formatDate(detailEntry.scheduledFor)}</span></div>
                <div className="detail-row"><span className="detail-label">Verarbeitet:</span><span className="detail-value">{formatDate(detailEntry.processedAt)}</span></div>
                <div className="detail-row"><span className="detail-label">Versuche:</span><span className="detail-value">{detailEntry.attempts} / {detailEntry.maxAttempts}</span></div>
                {detailEntry.errorMessage && (
                  <div className="detail-row detail-error"><span className="detail-label">Fehler:</span><span className="detail-value error-text">{detailEntry.errorMessage}</span></div>
                )}
              </div>
              {detailEntry.templateBodyPreview && (
                <div className="detail-body-preview">
                  <h3>E-Mail-Vorlage (Preview)</h3>
                  <div className="body-preview-content" dangerouslySetInnerHTML={{ __html: detailEntry.templateBodyPreview }} />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetailModal(false)}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {/* Email Correction Modal */}
      {showEmailCorrectionModal && (
        <div className="modal-overlay" onClick={() => setShowEmailCorrectionModal(false)}>
          <div className="modal-content email-correction-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>E-Mail-Adresse korrigieren</h2>
              <button className="modal-close" onClick={() => setShowEmailCorrectionModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="correction-hint">Die E-Mail-Adresse wird in den Kundendaten geändert und die E-Mail erneut gesendet.</p>
              <div className="correction-form">
                <label htmlFor="correction-email">Neue E-Mail-Adresse:</label>
                <input id="correction-email" type="email" value={correctionEmail} onChange={(e) => setCorrectionEmail(e.target.value)}
                  placeholder="korrekte@email.de" className="correction-input" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowEmailCorrectionModal(false)}>Abbrechen</button>
              <button className="btn-primary" onClick={handleSubmitEmailCorrection} disabled={!correctionEmail || actionLoading !== null}>
                {actionLoading ? 'Wird geändert...' : 'Adresse ändern & senden'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailQueueManagement;
